import React, { useState, useEffect, useMemo } from 'react';
import { Form, Input, InputNumber, Select, Button, message, Spin, Card, AutoComplete, Tag } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../utils/api';
import { theme } from '../../theme';
import { EnvironmentOutlined } from '@ant-design/icons';

const { Option } = Select;

interface SiloBolsa {
  id?: number;
  cargaenkilos: number;
  ubicacion: string;
  observaciones?: string;
  id_producto: number;
  id_empresa: number;
  id_sucursal: number;
}

// Función para normalizar texto (capitalizar primera letra de cada palabra)
const normalizeText = (text: string): string => {
  if (!text) return '';
  return text
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Función para obtener ubicaciones frecuentes desde localStorage
const getFrequentLocations = (): string[] => {
  try {
    const saved = localStorage.getItem('silobolsa_frequent_locations');
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (error) {
    console.error('Error al cargar ubicaciones frecuentes:', error);
  }
  return [];
};

// Función para guardar ubicación frecuente en localStorage
const saveFrequentLocation = (ubicacion: string): void => {
  try {
    const frequent = getFrequentLocations();
    const normalized = normalizeText(ubicacion);
    
    // Remover si ya existe y agregar al inicio
    const filtered = frequent.filter(loc => loc.toLowerCase() !== normalized.toLowerCase());
    const updated = [normalized, ...filtered].slice(0, 10); // Mantener solo las 10 más recientes
    
    localStorage.setItem('silobolsa_frequent_locations', JSON.stringify(updated));
  } catch (error) {
    console.error('Error al guardar ubicación frecuente:', error);
  }
};

interface SiloBolsaListado {
  id: number;
  ubicacion: string;
}

const CrearSilobolsa: React.FC = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [ubicacionesExistentes, setUbicacionesExistentes] = useState<string[]>([]);
  const [loadingUbicaciones, setLoadingUbicaciones] = useState(false);

  const id_empresa = user?.id_empresa;
  const id_sucursal = user?.id_sucursal;

  // Cargar ubicaciones existentes y frecuentes
  useEffect(() => {
    const cargarUbicaciones = async () => {
      if (!id_empresa || !id_sucursal) return;
      
      setLoadingUbicaciones(true);
      try {
        const token = await getAccessTokenSilently();
        const caso = "B";
        const response = await apiClient.get<{ silos_bolsas: SiloBolsaListado[] }>(
          `${process.env.REACT_APP_SILOBOLSA_URL}/listado_silobolsa/${id_empresa}/${id_sucursal}/${caso}/`,
          { token }
        );

        // Extraer ubicaciones únicas y normalizadas
        const ubicacionesUnicas = Array.from(
          new Set(
            response.data.silos_bolsas
              .map(s => s.ubicacion)
              .filter(ubic => ubic && ubic.trim())
              .map(ubic => normalizeText(ubic))
          )
        ).sort();

        setUbicacionesExistentes(ubicacionesUnicas);
      } catch (error) {
        console.error('Error al cargar ubicaciones:', error);
        // Si falla, usar solo las frecuentes guardadas
        setUbicacionesExistentes(getFrequentLocations());
      } finally {
        setLoadingUbicaciones(false);
        setInitialLoading(false);
      }
    };

    cargarUbicaciones();
  }, [id_empresa, id_sucursal, getAccessTokenSilently]);

  // Opciones de autocompletado combinando existentes y frecuentes
  const ubicacionOptions = useMemo(() => {
    const frecuentes = getFrequentLocations();
    const todas = [...frecuentes, ...ubicacionesExistentes];
    const unicas = Array.from(new Set(todas.map(u => u.toLowerCase())))
      .map(lower => {
        const original = todas.find(u => u.toLowerCase() === lower);
        return original || lower;
      });

    return unicas.map(ubicacion => ({
      value: ubicacion,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <EnvironmentOutlined style={{ color: theme.colors.primary }} />
          <span>{ubicacion}</span>
          {frecuentes.includes(ubicacion) && (
            <Tag color="blue" style={{ marginLeft: 'auto', fontSize: '10px' }}>
              Frecuente
            </Tag>
          )}
        </div>
      )
    }));
  }, [ubicacionesExistentes]);

  const onFinish = async (values: unknown) => {
    const formValues = values as Record<string, unknown>;
    
    setLoading(true);
    try {
      if (!id_empresa || !id_sucursal) {
        message.error('No se encontraron datos de empresa/sucursal');
        return;
      }

      // Normalizar ubicación antes de guardar
      const ubicacionNormalizada = normalizeText(String(formValues.ubicacion));
      
      const siloBolsaData: SiloBolsa = {
        cargaenkilos: Number(formValues.cargaenkilos),
        ubicacion: ubicacionNormalizada,
        observaciones: formValues.observaciones ? String(formValues.observaciones) : '',
        id_producto: 11, // Producto predeterminado
        id_empresa,
        id_sucursal
      };

      const token = await getAccessTokenSilently();
      
      await apiClient.post(
        `${process.env.REACT_APP_SILOBOLSA_URL}/crear_silobolsa/${id_empresa}/${id_sucursal}/`,
        siloBolsaData,
        { token }
      );

      message.success({
        content: 'Silo bolsa creado exitosamente. Puede continuar cargando más registros.',
        duration: 4,
      });
      
      // Guardar ubicación como frecuente
      saveFrequentLocation(ubicacionNormalizada);
      
      // Actualizar lista de ubicaciones existentes
      if (!ubicacionesExistentes.includes(ubicacionNormalizada)) {
        setUbicacionesExistentes([...ubicacionesExistentes, ubicacionNormalizada].sort());
      }
      
      // Reiniciar el formulario para permitir cargar otro registro
      form.resetFields();
      
      // Restablecer el valor inicial del producto
      form.setFieldsValue({ id_producto: 11 });
      
      // Scroll al inicio del formulario para mejor UX
      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (error: unknown) {
      console.error('Error al crear silo bolsa:', error);
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'Error al crear el silo bolsa';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/listado_silobolsa');
  };

  if (initialLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Card
        style={{
          borderRadius: theme.styles.borderRadius,
          boxShadow: theme.styles.boxShadow,
          maxWidth: '100%',
          marginBottom: '24px'
        }}
        styles={{
          header: {
            backgroundColor: theme.colors.primaryDark,
            color: 'white',
            borderRadius: '8px 8px 0 0',
            fontSize: '16px',
            fontWeight: 600
          }
        }}
        title="Información del Silo Bolsa"
      >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        style={{ 
          maxWidth: '100%',
          padding: '24px'
        }}
        className="silobolsa-form"
      >
        <style>{`
          .silobolsa-form .ant-form-item-label > label {
            color: #374151 !important;
            font-weight: 500 !important;
            font-size: 14px !important;
          }
          .silobolsa-form .ant-form-item-required::before {
            color: ${theme.colors.error} !important;
          }
          .silobolsa-form .ant-input,
          .silobolsa-form .ant-select-selector,
          .silobolsa-form .ant-input-number {
            border-color: #d1d5db !important;
            border-radius: 6px !important;
          }
          .silobolsa-form .ant-input:focus,
          .silobolsa-form .ant-select-selector:focus,
          .silobolsa-form .ant-input-number:focus {
            border-color: ${theme.colors.primary} !important;
            box-shadow: 0 0 0 2px rgba(45, 92, 146, 0.2) !important;
          }
        `}</style>
        
        {/* Grid de 3 columnas - Patrón del AltaPadron */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
          
          {/* Carga en Kilos */}
          <Form.Item
            name="cargaenkilos"
            label="Carga en Kilos"
            rules={[{ required: true, message: 'Ingrese la carga en kilos' }]}
          >
            <InputNumber
              placeholder="Ej: 1000"
              style={{ width: '100%' }}
              min={0}
              precision={2}
            />
          </Form.Item>

          {/* Ubicación con Autocompletado */}
          <Form.Item
            name="ubicacion"
            label="Ubicación"
            rules={[{ required: true, message: 'Ingrese la ubicación' }]}
            extra={
              <div style={{ fontSize: '12px', color: theme.colors.textSecondary, marginTop: '4px' }}>
                <EnvironmentOutlined style={{ marginRight: '4px' }} />
                Escriba para ver sugerencias o seleccione una ubicación existente
              </div>
            }
          >
            <AutoComplete
              options={ubicacionOptions}
              placeholder="Ej: Sector A, Playa Norte"
              maxLength={255}
              filterOption={(inputValue, option) =>
                option?.value?.toLowerCase().includes(inputValue.toLowerCase()) || false
              }
              onSelect={(value) => {
                // Normalizar al seleccionar
                form.setFieldsValue({ ubicacion: normalizeText(value) });
              }}
              onChange={(value) => {
                // Normalizar mientras se escribe
                if (value) {
                  form.setFieldsValue({ ubicacion: normalizeText(value) });
                }
              }}
              onBlur={() => {
                // Normalizar al perder el foco
                const currentValue = form.getFieldValue('ubicacion');
                if (currentValue) {
                  form.setFieldsValue({ ubicacion: normalizeText(currentValue) });
                }
              }}
              style={{ width: '100%' }}
              notFoundContent={loadingUbicaciones ? 'Cargando...' : 'Sin sugerencias'}
              allowClear
            />
          </Form.Item>

          {/* Producto */}
          <Form.Item
            name="id_producto"
            label="Producto"
            initialValue={11}
          >
            <Select 
              placeholder="Producto predeterminado" 
              disabled={true}
              value={11}
            >
              <Option value={11}>SEMILLA ALGODON</Option>
            </Select>
          </Form.Item>

          {/* Observaciones - Ocupa 3 columnas */}
          <Form.Item
            name="observaciones"
            label="Observaciones"
            rules={[{ required: false }]}
            style={{ gridColumn: '1 / -1' }}
          >
            <Input.TextArea
              placeholder="Observaciones adicionales (opcional)"
              rows={3}
              maxLength={255}
              showCount
            />
          </Form.Item>

        </div>

        {/* Botones de acción */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-end',
          gap: '12px',
          marginTop: '32px',
          paddingTop: '24px',
          borderTop: `1px solid ${theme.colors.borderLight}`
        }}>
          <Button
            onClick={handleCancel}
            size="small"
            style={{
              fontWeight: 500,
              minWidth: '120px'
            }}
          >
            Cancelar
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            size="small"
            style={{
              backgroundColor: theme.colors.secondary,
              borderColor: theme.colors.secondary,
              fontWeight: 500,
              minWidth: '120px'
            }}
          >
            {loading ? 'Guardando...' : 'Guardar'}
          </Button>
        </div>

      </Form>
    </Card>
    </div>
  );
};

export default CrearSilobolsa;
